import React, {useState, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store/rootReducer';
import {getCookie} from 'typescript-cookie';
import {
    UserAddOutlined,
    FundOutlined,
    WalletOutlined,
    NotificationOutlined,
    HomeOutlined,
    FormOutlined,
    MenuOutlined,
    TeamOutlined,
    RocketOutlined
} from '@ant-design/icons';
import {Layout, Menu, ConfigProvider, Button, Drawer} from 'antd';
import type {MenuProps} from 'antd';
import {NavLink, useLocation, useNavigate} from 'react-router-dom';
import FeedbackPage from "../../../pages/Feedback/page";
import {useIsMobile} from "../../../utils/helpers";
import {showSubscriptionModal} from '../Modals/subscription/component';

const {Sider} = Layout;

type MenuItem = Required<MenuProps>['items'][number];

const getItem = (
    label: React.ReactNode,
    key: string,
    icon?: React.ReactNode,
    children?: MenuItem[]
): MenuItem => ({
    key,
    icon,
    children,
    label
});

const roleBasedItems: Record<string, MenuItem[]> = {
    advertiser: [
        getItem(<NavLink to="/home">Dashboard</NavLink>, '/home', <HomeOutlined/>),
        getItem('Campaigns', 'campaigns', <FundOutlined/>, [
            getItem(<NavLink to="/campaigns/create">Create campaign</NavLink>, '/campaigns/create'),
            getItem(<NavLink to="/campaigns/list">My campaigns</NavLink>, '/campaigns/list')
        ]),
        getItem(<NavLink to="/campaign-requests">Campaign requests</NavLink>, '/campaign-requests', <TeamOutlined/>),
        getItem('Wallet', 'wallet', <WalletOutlined/>, [
            getItem(<NavLink to="/wallet">Wallet details</NavLink>, '/wallet'),
            getItem(<NavLink to="/wallet/transactions">Transactions</NavLink>, '/wallet/transactions')
        ]),
        getItem('Feed', 'feed', <NotificationOutlined/>, [
            getItem(<NavLink to="/feed/best-advertisers">Best advertisers</NavLink>, '/feed/best-advertisers'),
            getItem(<NavLink to="/feed/best-promoters">Best promoters</NavLink>, '/feed/best-promoters'),
            getItem(<NavLink to="/feed/best-partners">Best partners</NavLink>, '/feed/best-partners')
        ])
    ],
    promoter: [
        getItem(<NavLink to="/home">Dashboard</NavLink>, '/home', <HomeOutlined/>),
        getItem('Campaigns', 'campaigns', <FundOutlined/>, [
            getItem(<NavLink to="/campaign-links/list">My campaigns</NavLink>, '/campaign-links/list'),
            getItem(<NavLink to="/campaigns/browse">Browse campaigns</NavLink>, '/campaigns/browse')
        ]),
        getItem('Wallet', 'wallet', <WalletOutlined/>, [
            getItem(<NavLink to="/wallet">Wallet details</NavLink>, '/wallet'),
            getItem(<NavLink to="/wallet/transactions">Transactions</NavLink>, '/wallet/transactions')
        ]),
        getItem('Feed', 'feed', <NotificationOutlined/>, [
            getItem(<NavLink to="/feed/best-advertisers">Best advertisers</NavLink>, '/feed/best-advertisers'),
            getItem(<NavLink to="/feed/best-promoters">Best promoters</NavLink>, '/feed/best-promoters'),
            getItem(<NavLink to="/feed/best-partners">Best partners</NavLink>, '/feed/best-partners')
        ])
    ],
    partner: [
        getItem(<NavLink to="/home">Dashboard</NavLink>, '/home', <HomeOutlined/>),
        getItem('Campaigns', 'campaigns', <FundOutlined/>, [
            getItem(<NavLink to="/campaign-links/list">My campaigns</NavLink>, '/campaign-links/list'),
            getItem(<NavLink to="/campaigns/browse">Browse campaigns</NavLink>, '/campaigns/browse')
        ]),
        getItem('Wallet', 'wallet', <WalletOutlined/>, [
            getItem(<NavLink to="/wallet">Wallet details</NavLink>, '/wallet'),
            getItem(<NavLink to="/wallet/transactions">Transactions</NavLink>, '/wallet/transactions')
        ]),
        getItem('Feed', 'feed', <NotificationOutlined/>, [
            getItem(<NavLink to="/feed/best-advertisers">Best advertisers</NavLink>, '/feed/best-advertisers'),
            getItem(<NavLink to="/feed/best-promoters">Best promoters</NavLink>, '/feed/best-promoters'),
            getItem(<NavLink to="/feed/best-partners">Best partners</NavLink>, '/feed/best-partners')
        ]),
        getItem(<NavLink to="/invite-users">Invite users</NavLink>, '/invite-users', <UserAddOutlined/>)
    ]
};

type Props = {
    renderMobileTrigger?: (open: () => void) => React.ReactNode;
};


const SideBar: React.FC<Props> = ({renderMobileTrigger}) => {
    const isMobile = useIsMobile();

    const [collapsed, setCollapsed] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    const currentUser: any = useSelector((state: RootState) => state.auth.user);
    const role = getCookie('roleType') || currentUser?.roles?.[0]?.roleType || 'advertiser';
    const currentRole = currentUser?.roles?.find((r: any) => r.roleType === role);
    const shouldShowStartTrial = Boolean(currentRole && !currentRole.isActive && !currentRole.subscription?.id);

    const menuItems: MenuItem[] = useMemo(() => {
        const items = roleBasedItems[role] ? [...roleBasedItems[role]] : [];
        if (shouldShowStartTrial) {
            items.push(getItem("Start trial", "start-trial", <RocketOutlined/>));
        }
        items.push(getItem("Send feedback", "feedback", <FormOutlined/>));
        return items;
    }, [role, shouldShowStartTrial]);

    useEffect(() => {
        if (!isMobile) setDrawerOpen(false);
    }, [isMobile]);

    const handleMenuClick: MenuProps["onClick"] = ({key}) => {
        if (key === "start-trial") {
            showSubscriptionModal();
            setDrawerOpen(false);
            return;
        }

        if (key === "feedback") {
            setShowFeedback(true);
            setDrawerOpen(false);
            return;
        }

        if (typeof key === "string" && key.startsWith("/")) {
            navigate(key);
            setDrawerOpen(false);
        }
    };

    const menuNode = (
        <ConfigProvider>
            <Menu
                theme="light"
                mode="inline"
                selectedKeys={[location.pathname]}
                items={menuItems}
                style={{marginTop: 20}}
                onClick={handleMenuClick}
            />
        </ConfigProvider>
    );

    return (
        <>
            {isMobile ? (
                <>
                    {renderMobileTrigger ? (
                        renderMobileTrigger(() => setDrawerOpen(true))
                    ) : (
                        <Button
                            type="text"
                            icon={<MenuOutlined/>}
                            onClick={() => setDrawerOpen(true)}
                            style={{position: "fixed", top: 12, left: 12, zIndex: 1001}}
                            aria-label="Open menu"
                        />
                    )}

                    <Drawer
                        open={drawerOpen}
                        onClose={() => setDrawerOpen(false)}
                        placement="left"
                        width={260}
                        bodyStyle={{padding: 0}}
                    >
                        {menuNode}
                    </Drawer>
                </>
            ) : (
                <Sider
                    theme="light"
                    width={220}
                    collapsedWidth={64}
                    breakpoint="md"
                    onBreakpoint={(broken) => setCollapsed(broken)}
                    collapsed={collapsed}
                    style={{marginRight: 20}}
                >
                    <div className="demo-logo-vertical"/>
                    {menuNode}
                </Sider>
            )}

            <FeedbackPage open={showFeedback} onClose={() => setShowFeedback(false)}/>
        </>
    );
};

export default SideBar;
